// Lo que el CRM necesita para atender el portal del cliente: contratos,
// documentos, incidencias y solicitudes de vinculación.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Client } from './clients';

export interface Contrato {
  contract_id: string;
  property_id: string;
  tenant_client_id: string;
  fecha_inicio: string;
  fecha_fin?: string;
  renta?: number;
  fianza?: number;
  estado: 'vigente' | 'finalizado';
  notas?: string;
}

export interface Documento {
  document_id: string;
  ref_id: string;
  nombre: string;
  tipo?: string;
  visible_cliente?: boolean;
  created_at?: string;
}

export interface Incidencia {
  incident_id: string;
  property_id: string;
  client_id: string;
  categoria: string;
  descripcion: string;
  estado: 'abierta' | 'en_curso' | 'resuelta';
  respuesta?: string;
  fotos?: string[];
  created_at: string;
}

// ── Contratos ─────────────────────────────────────────────────────
//
// Dar de alta un contrato es lo que le abre el portal al inquilino: hasta que
// existe, entra y no ve nada suyo.

export function useContratosDeVivienda(propertyId: string | null) {
  return useQuery<Contrato[]>({
    queryKey: ['contracts', 'property', propertyId],
    queryFn: async () =>
      (await api.get(`/crm/contracts?property_id=${propertyId}`)).data.items,
    enabled: Boolean(propertyId),
  });
}

export function useGuardarContrato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (c: Partial<Contrato> & { contract_id?: string }) => {
      const { contract_id, ...body } = c;
      const res = contract_id
        ? await api.put(`/crm/contracts/${contract_id}`, body)
        : await api.post('/crm/contracts', body);
      return res.data as Contrato;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });
}

// ── Documentos ────────────────────────────────────────────────────

export function useDocumentos(refId: string | null) {
  return useQuery<Documento[]>({
    queryKey: ['documents', refId],
    queryFn: async () => (await api.get(`/crm/documents?ref_id=${refId}`)).data.items,
    enabled: Boolean(refId),
  });
}

export function useSubirDocumento(refId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, tipo }: { file: File; tipo: string }) => {
      const { data } = await api.post('/crm/documents', {
        ref_id: refId,
        nombre: file.name,
        tipo,
        content_type: file.type,
      });
      // Igual que las fotos: el fichero va directo a S3 y no pasa por Lambda.
      await fetch(data.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', refId] }),
  });
}

export function useBorrarDocumento(refId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) =>
      api.delete(`/crm/documents/${documentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', refId] }),
  });
}

// ── Incidencias ───────────────────────────────────────────────────

export function useIncidencias() {
  return useQuery<Incidencia[]>({
    queryKey: ['incidents'],
    queryFn: async () => (await api.get('/crm/incidents')).data.items,
  });
}

export function useActualizarIncidencia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      incidentId,
      estado,
      respuesta,
    }: {
      incidentId: string;
      estado?: string;
      respuesta?: string;
    }) => (await api.patch(`/crm/incidents/${incidentId}`, { estado, respuesta })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents'] }),
  });
}

// ── Vinculaciones pendientes ──────────────────────────────────────

export function useSolicitudesVinculacion() {
  return useQuery<Client[]>({
    queryKey: ['link-requests'],
    queryFn: async () => (await api.get('/crm/link-requests')).data.items,
  });
}

export function useResolverVinculacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, aceptar }: { clientId: string; aceptar: boolean }) =>
      (await api.post(
        `/crm/clients/${clientId}/link/${aceptar ? 'aprobar' : 'rechazar'}`,
        {},
      )).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['link-requests'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
